import { type ReactNode } from 'react';
import { Divider, Typography } from '@mui/material';
import { RuleType, type MarkdownToJSX } from 'markdown-to-jsx';
import { getOverrides, type Options, type Overrides } from 'mui-markdown';
import { KatexSpan } from './KatexSpan';
import { MarkdownTable } from './MarkdownTable';

// $$...$$ (block) or $...$ (inline). Genealogy notes routinely use a bare `$`
// as a currency sign ("dot de 200$"), so a span only counts as math when it
// contains a LaTeX command: "$200$" stays plain text, "$\rightarrow$" renders.
const MATH_PATTERN = /\$\$([^$]*\\[^$]*)\$\$|\$([^$\n]*\\[^$\n]*)\$/g;

function renderTextWithMath(text: string, key: string | number | undefined): ReactNode {
    if (!text.includes('$') || !text.includes('\\')) return text;

    const parts: ReactNode[] = [];
    let lastIndex = 0;
    let mathIndex = 0;
    let match: RegExpExecArray | null;
    MATH_PATTERN.lastIndex = 0;
    while ((match = MATH_PATTERN.exec(text))) {
        if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
        const [, blockExpression, inlineExpression] = match;
        const expression = (blockExpression ?? inlineExpression ?? '').trim();
        parts.push(<KatexSpan key={`${key}-math-${mathIndex++}`} expression={expression} displayMode={blockExpression !== undefined} />);
        lastIndex = MATH_PATTERN.lastIndex;
    }
    if (parts.length === 0) return text;
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts;
}

/** markdown-to-jsx `renderRule`: renders `$...$`/`$$...$$` spans through KaTeX
 * and leaves every other node (including code blocks) to the default renderer. */
function renderRuleWithMath(
    next: () => ReactNode,
    node: MarkdownToJSX.ASTNode,
    _renderChildren: MarkdownToJSX.ASTRender,
    state: MarkdownToJSX.State,
): ReactNode {
    if (node.type === RuleType.text) {
        return renderTextWithMath(node.text, state.key);
    }
    return next();
}

/** mui-markdown maps `##`/`###`/... straight to MUI's `h2`/`h3`/... Typography
 * variants, which are sized for page titles, not note subheadings — much too
 * big in the context these notes render in. Scaled down to text-sized variants. */
const HEADING_OVERRIDES: Overrides = {
    h1: { component: Typography, props: { component: 'h1', variant: 'h4', sx: { fontWeight: 700, mt: 1.5, mb: 1 } } },
    h2: { component: Typography, props: { component: 'h2', variant: 'h5', sx: { fontWeight: 700, mt: 1.5, mb: 1 } } },
    h3: { component: Typography, props: { component: 'h3', variant: 'h6', sx: { fontWeight: 700, mt: 1.5, mb: 1 } } },
    h4: { component: Typography, props: { component: 'h4', variant: 'subtitle1', sx: { fontWeight: 700, mt: 1, mb: 0.75 } } },
    h5: { component: Typography, props: { component: 'h5', variant: 'subtitle2', sx: { fontWeight: 700, fontStyle: 'italic', mt: 1, mb: 0.75 } } },
    h6: { component: Typography, props: { component: 'h6', variant: 'body1', sx: { fontWeight: 700, fontStyle: 'italic', mt: 1, mb: 0.75 } } },
    hr: { component: Divider, props: { sx: { mt: 1.5, mb: 1.5 } } },
    table: { component: MarkdownTable },
};

/** `<MuiMarkdown>` options that add LaTeX rendering on top of mui-markdown's
 * own overrides (plus any caller-supplied ones) — `options` and `overrides`
 * are mutually exclusive props on `<MuiMarkdown>`, so this is the plumbing
 * needed to use both mui-markdown's styling and a custom `renderRule`. */
export function getMathMarkdownOptions(overrides?: Overrides): Options {
    return {
        overrides: getOverrides({ overrides: { ...HEADING_OVERRIDES, ...overrides } }),
        renderRule: renderRuleWithMath,
    };
}
